'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, FileUp, BarChart3 } from 'lucide-react';

export default function QuickStatisticsPage() {
  const [csvInput, setCsvInput] = useState('');
  const [stats, setStats] = useState('');
  const [columnIndex, setColumnIndex] = useState('0');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const calculateStats = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 2) {
        setError('CSV must have at least a header row and one data row');
        return;
      }

      const colIdx = parseInt(columnIndex);
      const dataRows = lines.slice(1).map(line => line.split(',').map(v => v.trim()));
      const values = dataRows.map(row => parseFloat(row[colIdx])).filter(v => !isNaN(v));

      if (values.length === 0) {
        setError('Selected column contains no numeric data');
        return;
      }

      values.sort((a, b) => a - b);
      const sum = values.reduce((a, b) => a + b, 0);
      const mean = sum / values.length;
      const min = values[0];
      const max = values[values.length - 1];
      const median = values.length % 2 === 0 
        ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2 
        : values[Math.floor(values.length / 2)];

      let result = `Statistics for Column ${columnIndex}:\n`;
      result += `- Count: ${values.length}\n`;
      result += `- Mean: ${mean.toFixed(2)}\n`;
      result += `- Median: ${median.toFixed(2)}\n`;
      result += `- Min: ${min}\n`;
      result += `- Max: ${max}\n`;
      result += `- Range: ${max - min}\n`;

      setStats(result);
    } catch (err) {
      setError('Error calculating statistics. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(stats);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Quick Statistics
              </h1>
            <p className="text-lg text-muted-foreground">
              Get instant descriptive statistics for any numeric column in your CSV
            </p>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="mb-6 max-w-xs">
            <label className="text-sm font-medium mb-2 block">Column Index (0-based)</label>
            <Input 
              type="number" 
              value={columnIndex} 
              onChange={(e) => setColumnIndex(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="ID,Score&#10;1,85&#10;2,90&#10;3,78"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={calculateStats} 
                className="w-full"
                size="lg"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Calculate Statistics
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Statistics Output</label>
                <Textarea
                  value={stats}
                  readOnly
                  placeholder="Your statistics will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!stats}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Quick Statistics FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. What statistics are calculated?</p><p className="text-muted-foreground">The tool calculates count, mean (average), median, minimum, maximum, and range.</p></div>
          <div><p className="font-semibold">2. How are non-numeric values handled?</p><p className="text-muted-foreground">Any value that cannot be parsed as a number is ignored in the calculations.</p></div>
          <div><p className="font-semibold">3. Can I analyze multiple columns at once?</p><p className="text-muted-foreground">Currently, you can analyze one column at a time by changing the column index.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
